// Authentication context provider

import { createContext, useState, useCallback, useEffect, useRef, ReactNode, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { User, FeaturePermissions, UserOrg } from './types';
import { AuthApi } from './api';
import { ApiClientError } from '@/lib/axios/client';
import { Organization } from '../organization/types';
import { identifyUser, resetAnalyticsUser } from '@/lib/analytics/track';

// Query key for auth state
const AUTH_QUERY_KEY = ['auth', 'me'] as const;

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  currentOrg: Organization | null;
  userPermission: 'read' | 'read_write' | 'admin' | null;
  featurePermissions: FeaturePermissions | null;
  permissionsError: Error | null;
  login: (mobile: string, stdCode: string) => Promise<void>;
  verifyOTP: (mobile: string, stdCode: string, otp: number) => Promise<void>;
  logout: () => Promise<void>;
  setCurrentOrg: (org: Organization | null) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider component
 * Provides authentication state and methods to the application
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const cookiePrefix = import.meta.env.VITE_COOKIE_PREFIX
  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(() => {
    const storedOrg = localStorage.getItem(`${cookiePrefix}_current_org`);
    if (storedOrg) {
      try {
        return JSON.parse(storedOrg) as Organization;
      } catch {
        return null;
      }
    }
    return null;
  });
  const [userPermission, setUserPermission] = useState<'read' | 'read_write' | 'admin' | null>(null);
  const [featurePermissions, setFeaturePermissions] = useState<FeaturePermissions | null>(null);
  const [permissionsError, setPermissionsError] = useState<Error | null>(null);
  const permissionsRequestId = useRef(0);

  // Use React Query to check auth status - handles caching, deduplication, and loading states
  const { data: user, isLoading } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: () => AuthApi.getMe(),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
  });

  // Only the latest request counts: one superseded by a later switch resolves to null, so
  // a slow response for an org the user has since left can't overwrite the one they picked.
  const fetchOrgPermissions = useCallback(async (org: Organization): Promise<UserOrg | null> => {
    const requestId = ++permissionsRequestId.current;
    try {
      const response = await AuthApi.switchOrg(org.id);
      return requestId === permissionsRequestId.current ? response : null;
    } catch (error) {
      if (requestId !== permissionsRequestId.current) return null;
      throw error;
    }
  }, []);

  const applyOrgPermissions = useCallback((response: UserOrg) => {
    setUserPermission(response.permissions);
    setFeaturePermissions(response.feature_permissions ?? null);
    setPermissionsError(null);
  }, []);

  const failOrgPermissions = useCallback((error: unknown) => {
    console.error('Failed to fetch org permissions:', error);
    setUserPermission('read_write'); // Default fallback
    setFeaturePermissions(null);
    setPermissionsError(error instanceof Error ? error : new Error('Failed to load permissions'));
  }, []);

  // Load current org from localStorage and fetch permissions
  useEffect(() => {
    const loadStoredOrg = async () => {
      const storedOrg = localStorage.getItem(`${cookiePrefix}_current_org`);
      if (storedOrg) {
        try {
          const org = JSON.parse(storedOrg) as Organization;
          setCurrentOrgState(org);

          try {
            const response = await fetchOrgPermissions(org);
            if (response) applyOrgPermissions(response);
          } catch (error) {
            // The stored org is no longer one of the user's (removed, or another user's
            // org on a shared browser): forget it so they are sent to pick one. Super
            // admins get 403 for orgs they aren't members of, so theirs is kept.
            if (error instanceof ApiClientError && error.status === 403 && !user?.is_super_admin) {
              localStorage.removeItem(`${cookiePrefix}_current_org`);
              localStorage.removeItem(`${cookiePrefix}_org_id`);
              setCurrentOrgState(null);
            } else {
              failOrgPermissions(error);
            }
          }
        } catch (e) {
          console.error('Failed to parse stored organization', e);
        }
      }
    };

    // Only load org if user is authenticated
    if (user) {
      loadStoredOrg();
    }
  }, [cookiePrefix, user, fetchOrgPermissions, applyOrgPermissions, failOrgPermissions]);

  // Identify the user for analytics once authenticated so backend and
  // frontend events merge onto one PostHog person timeline.
  useEffect(() => {
    if (user) {
      identifyUser(user.id, { org_id: user.org_id, name: user.name });
    }
  }, [user]);

  const login = useCallback(async (mobile: string, stdCode: string) => {
    await AuthApi.login(mobile, stdCode);
  }, []);

  const verifyOTP = useCallback(
    async (mobile: string, stdCode: string, otp: number) => {
      const { user } = await AuthApi.verifyOTP(mobile, stdCode, otp);
      // Update the auth query cache with the new user
      queryClient.setQueryData(AUTH_QUERY_KEY, user);
    },
    [queryClient]
  );

  const logout = useCallback(async () => {
    try {
      await AuthApi.logout();
    } finally {
      // Clear auth query cache
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      setCurrentOrgState(null);
      permissionsRequestId.current++;
      setPermissionsError(null);
      setUserPermission(null);
      setFeaturePermissions(null);
      localStorage.removeItem(`${cookiePrefix}_current_org`);
      localStorage.removeItem(`${cookiePrefix}_org_id`);
      // Clear all cached queries to prevent stale data when a new user logs in
      queryClient.clear();
      resetAnalyticsUser();
    }
  }, [cookiePrefix, queryClient]);

  // Resolves true once org is current, or false if a later switch superseded it. On
  // failure it throws and leaves the previous org in place, except for super admins:
  // switch-org refuses orgs they aren't members of, yet they may open any org.
  const setCurrentOrg = useCallback(async (org: Organization | null) => {
    if (!org) {
      permissionsRequestId.current++;
      setPermissionsError(null);
      setCurrentOrgState(null);
      localStorage.removeItem(`${cookiePrefix}_current_org`);
      localStorage.removeItem(`${cookiePrefix}_org_id`);
      setUserPermission(null);
      setFeaturePermissions(null);
      return true;
    }
    const makeCurrent = () => {
      setCurrentOrgState(org);
      localStorage.setItem(`${cookiePrefix}_current_org`, JSON.stringify(org));
      localStorage.setItem(`${cookiePrefix}_org_id`, org.id);
    };
    try {
      const response = await fetchOrgPermissions(org);
      if (!response) return false;
      makeCurrent();
      applyOrgPermissions(response);
    } catch (error) {
      if (!user?.is_super_admin) throw error;
      makeCurrent();
      failOrgPermissions(error);
    }
    return true;
  }, [cookiePrefix, user, fetchOrgPermissions, applyOrgPermissions, failOrgPermissions]);

  const value = useMemo(()=> ({
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading,
    currentOrg,
    userPermission,
    featurePermissions,
    permissionsError,
    login,
    verifyOTP,
    logout,
    setCurrentOrg,
  }),[user, login, logout, verifyOTP, isLoading, currentOrg, userPermission, featurePermissions, permissionsError, setCurrentOrg]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
